import React, { useCallback } from 'react';
import styles from './index.module.scss';
import CatergoriesBar from './categories';
import ProductNav from './product-nav';
import { FlattenedProductProps } from '../../../models';
import { useAppSelector } from '../../../store/storeConfig';
import { useBarCodeScan } from '../../../hooks/useBarCodeScan';
import EmptyProductList from './empty-list';
import { SearchBar } from './search-bar';
import VirtualizeGrid from '../virtualized-products/grid';
import VirtualizeList from '../virtualized-products/list';
import _ from 'lodash';
import Fuse, { IFuseOptions } from 'fuse.js';
import { useGetProducts } from '../../../integrations/react_query/queries';
import { EQuerykeys } from '../../../models/index';
import CategoryListMobile from '../../resuable-ui/CategoryListMobile/index';
import CategoryFilterTrigger from '../../resuable-ui/CategoryFilterTrigger/index';
import ScanFAB from '../../resuable-ui/scan-fab';
import MobileBarcodeScanner from '../../resuable-ui/mobile-barcode-scanner';
import { useCart } from '../../../hooks/useCart';

const PRODUCTS_LOAD_LIMIT = 150;

export const Products = () => {
	let filteredData: FlattenedProductProps[] | null = null;

	const { isReplicatingProduct } = useAppSelector((state) => state.productSlice);
	const { handleAddToCart } = useCart();

	const [toggleView, setToggleView] = React.useState(true);
	const [showCategories, setShowCategories] = React.useState(false);
	const [selectedCategories, setSelectedCategories] = React.useState<string[]>([]);
	const [showMobileScanner, setShowMobileScanner] = React.useState(false);

	const { data, isLoading, isError, refetch } = useGetProducts([EQuerykeys.product], {
		refetchOnMount: true,
		refetchOnReconnect: true,
		refetchOnWindowFocus: true,
		keepPreviousData: true,
		retry: true,
		refetchIntervalInBackground: true,
		staleTime: Infinity,
		cacheTime: Infinity,
	});

	React.useEffect(() => {
		if (!isReplicatingProduct) {
			refetch();
		}
	}, [isReplicatingProduct, refetch]);

	const [value, setSearchValue] = React.useState('');
	const kdbRef = React.useRef<HTMLInputElement | null>(null);
	const [debouncedValue, setDebouncedValue] = React.useState('');

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedSearch = useCallback(
		_.debounce((val: string) => {
			setDebouncedValue(val);
		}, 250),
		[]
	);

	React.useEffect(() => {
		return () => {
			debouncedSearch.cancel();
		};
	}, [debouncedSearch]);

	const barcodeInputRef = useBarCodeScan({
		ref: kdbRef,
		setValue: setSearchValue,
		debouncedValue,
	});

	const [category, setCategory] = React.useState<null | string>(null);

	React.useEffect(() => {
		if (category) {
			debouncedSearch.cancel();
			setDebouncedValue('');
		}
	}, [category, debouncedSearch]);

	React.useEffect(() => {
		if (selectedCategories.length > 0) {
			setCategory(null);
			setSearchValue('');
			debouncedSearch.cancel();
		}
	}, [selectedCategories, debouncedSearch]);

	if (category) {
		if (category === 'all') {
			filteredData = data ?? null;
		} else {
			filteredData = (data || []).filter((item) => item?.categories?.includes(category));
		}
	} else if (selectedCategories.includes('__all__')) {
		filteredData = data ?? null;
	} else if (selectedCategories.length > 0) {
		filteredData = (data || []).filter((item) => selectedCategories.some((cat) => item?.categories?.includes(cat)));
	}

	if (debouncedValue && !barcodeInputRef) {
		const options: IFuseOptions<FlattenedProductProps> = {
			keys: ['productName', 'sku', 'description', 'tags'],
			threshold: 0.4,
			ignoreLocation: true,
			distance: 200,
			minMatchCharLength: 1,
		};

		const fuse = new Fuse(data || [], options);

		const barcodeMatch = (data || []).filter((item) => item?.barcode?.toString() === debouncedValue?.toString());

		const fuseResults = fuse.search(debouncedValue).map((res) => res.item);

		filteredData = [...barcodeMatch, ...fuseResults.filter((item) => !barcodeMatch.includes(item))];
	}

	if (barcodeInputRef) {
		filteredData = (data || []).filter((item) => item?.barcode?.toString() === debouncedValue?.toString());
	}

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		setSearchValue(val);
		setCategory(null);
		debouncedSearch(val);
	};

	React.useEffect(() => {
		if (kdbRef.current) {
			document.body.focus();
		}
	}, []);

	const parentRef = React.useRef<HTMLDivElement>(null);

	return (
		<section className={`${styles.wrapper} position-relative col-12 col-md-7 col-lg-7 col-xl-7 col-xxl-7`}>
			<ProductNav toggle={toggleView} setToggle={setToggleView}>
				<CategoryFilterTrigger
					selectedCount={selectedCategories.filter((c) => c !== '__all__').length}
					onClick={() => setShowCategories(true)}
				/>
				<SearchBar ref={kdbRef} onChange={handleChange} value={value} />
			</ProductNav>
			<div className={`${styles.productWrapper} pt-7 md:pt-14 md:scrollbar-auto scrollbar-hide`}>
				{isLoading ? (
					<section className="d-flex align-items-center justify-content-center" style={{ height: '30vh' }}>
						Pulling up data
					</section>
				) : isError ? (
					<div>An error occurred</div>
				) : (filteredData || []).length <= 0 || (!debouncedValue && !category && selectedCategories.length === 0) ? (
					<EmptyProductList />
				) : (
					<>
						{toggleView ? (
							<VirtualizeGrid products={(filteredData ?? []).slice(0, PRODUCTS_LOAD_LIMIT)} />
						) : (
							<VirtualizeList parentRef={parentRef} products={(filteredData ?? []).slice(0, PRODUCTS_LOAD_LIMIT)} />
						)}
					</>
				)}
			</div>

			{!isLoading && !isError && (
				<CategoryListMobile
					data={data ?? []}
					show={showCategories}
					setShow={setShowCategories}
					selectedCategories={selectedCategories}
					onSelectionChange={setSelectedCategories}
					setSearchValue={setSearchValue}
				/>
			)}

			<div className="hidden md:flex">
				{!isLoading && !isError && (
					<CatergoriesBar
						{...{
							category,
							productData: data ?? [],
							setCategory,
							setSearchValue,
						}}
					/>
				)}
			</div>

			<ScanFAB onClick={() => setShowMobileScanner(true)} isScanning={showMobileScanner} />
			<MobileBarcodeScanner
				isOpen={showMobileScanner}
				onClose={() => setShowMobileScanner(false)}
				onScanSuccess={(barcode) => {
					const scannedProduct = (data || []).find((item) => item?.barcode?.toString() === barcode?.toString());
					if (scannedProduct) {
						handleAddToCart({ ...scannedProduct, quantity: 1 });
					}

					setSearchValue(barcode);
					debouncedSearch(barcode);
				}}
			/>
		</section>
	);
};

export default Products;
